import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { StreetPageVGDisclaimerModal } from '../../components/StreetPageVGDisclaimerModal/StreetPageVGDisclaimerModal';

interface VGDisclaimerModal {
  name: 'vgDisclaimer';
}

type StreetPageModal = VGDisclaimerModal | null;

interface StreetPageModalContext {
  displayModal: (x: StreetPageModal) => void;
}

export const StreetPageModalContext = createContext<StreetPageModalContext | undefined>(undefined);

export const useStreetPageModalContext = (): StreetPageModalContext => {
  const context = useContext(StreetPageModalContext);
  if (context === undefined) {
    throw new Error('StreetPageModalContext must be used within a StreetPageModalContext.Provider');
  }
  return context;
};

interface StreetPageModalHandlerProps {
  modal: StreetPageModal;
  onClose: () => void;
}

export const StreetPageModalHandler: FunctionComponent<StreetPageModalHandlerProps> = ({
  modal,
  onClose,
}) => {
  switch (modal?.name) {
    case 'vgDisclaimer':
      return <StreetPageVGDisclaimerModal opened onClose={onClose} />;
    default:
      return null;
  }
};

export const StreetPageModalContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [modal, setModal] = useState<StreetPageModal>(null);
  const contextValue = useMemo(
    () => ({
      displayModal: (a: StreetPageModal) => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setModal(a);
          }, 0);
        });
      },
    }),
    [setModal],
  );

  return (
    <StreetPageModalContext.Provider value={contextValue}>
      {modal && <StreetPageModalHandler modal={modal} onClose={() => setModal(null)} />}
      {children}
    </StreetPageModalContext.Provider>
  );
};
